import {useState} from 'react';

import {Button} from 'shells/button/button';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {MobilePhoneRegularIcon} from 'shells/icon/svg/mobile-phone-regular-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';

import type {
    GetPeepBusinessCardInfo_peep_emailAddresses_all_deliverabilityStatus as EmailDeliverabilityStatus,
    GetPeepBusinessCardInfo_peep_phones_all_deliverabilityStatus as SmsDeliverabilityStatus,
} from 'nutshell-graphql-types';

import {SmsSidebarButton} from '../../../inbox/sidebar/sms/sms-sidebar-button';
import {PhoneSettingsDialog} from '../../../lead/shared/components/phone-settings-dialog';
import {DeliverabilityStatusBadge} from '../../../peep/shared/deliverability-status-badge';

import {handleCall} from '../../../../phone/handle-call';

import {checkPhoneIntegrationStatus} from '../../util/action-bar-helpers';

import type {Phone} from './types';

export type ConnectButtonsRowProps = {
    id: string;
    name?: string | null | undefined;
    phone?: Phone | null | undefined;
    email?: string | null | undefined;
    phoneCallerType?: string | null | undefined;
    userCanUseSms?: boolean | null | undefined;
    sendFromNutshellEnabled?: boolean | null | undefined;
    onOpenEmailModal?: (emailAddress: string) => void;
    hasConnectedPhoneIntegration?: boolean;
    deliverabilityStatus?: SmsDeliverabilityStatus | EmailDeliverabilityStatus | null | undefined;
};

export function ConnectButtonsRow(props: ConnectButtonsRowProps) {
    const {phone} = props;
    const deliverabilityStatus = props.deliverabilityStatus ?? null;
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);

    const callButtonId = `${props.id}-call-button-${encodeURIComponent(
        phone?.id || phone?.numberFormattedWithCountryCode || 'unknown-phone'
    )}`;

    const onCall = () => {
        if (phone && phone.numberFormatted && phone.id && props.phoneCallerType) {
            handleCall(
                phone.numberFormatted,
                phone.id,
                props.phoneCallerType,
                props.id,
                callButtonId
            );
        }
    };

    const handleCallButtonClicked = () => {
        checkPhoneIntegrationStatus(
            props.hasConnectedPhoneIntegration,
            props.phoneCallerType,
            () => setShowSettingsDialog(true),
            onCall
        );
    };

    const openEmailModal = (emailAddress: string) => {
        if (!props.email) {
            return;
        }

        if (props.sendFromNutshellEnabled === false) {
            window.open(`mailto:${emailAddress}?bcc=bcc@nutshell.com`);
            return;
        }

        if (props.onOpenEmailModal) {
            props.onOpenEmailModal(emailAddress);
        }
    };

    const isDeliverabilityRestricted =
        NutClientConfig.hasSmsAudiencesFF &&
        !!deliverabilityStatus &&
        deliverabilityStatus.status !== 'DELIVERABLE';

    const canCallWithCurrentDeliverabilityStatus =
        !isDeliverabilityRestricted ||
        (deliverabilityStatus?.__typename === 'SmsDeliverabilityStatus' &&
            deliverabilityStatus.status === 'OPTED_OUT');

    const shouldShowCallButton =
        phone &&
        phone.numberFormatted &&
        props.phoneCallerType !== 'kixie' &&
        canCallWithCurrentDeliverabilityStatus;

    const shouldShowSmsButton =
        phone &&
        phone.numberFormattedWithCountryCode &&
        phone.id &&
        props.userCanUseSms &&
        !isDeliverabilityRestricted;

    const shouldShowEmailButton =
        props.email && props.onOpenEmailModal && !isDeliverabilityRestricted;

    const shouldShowDeliverabilityStatusBadge =
        !!deliverabilityStatus && isDeliverabilityRestricted;

    if (!shouldShowCallButton && !shouldShowSmsButton && !shouldShowEmailButton) {
        if (shouldShowDeliverabilityStatusBadge) {
            return (
                <DeliverabilityStatusBadge
                    deliverabilityStatus={deliverabilityStatus}
                    iconOnly={false}
                />
            );
        }
        return null;
    }

    return (
        <div className='flex gap-4 mt-8'>
            {shouldShowCallButton && (
                <Button
                    id={callButtonId}
                    icon={PhoneOutlineIcon}
                    size='small'
                    variant='upgrade'
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCallButtonClicked();
                    }}
                >
                    {props.phoneCallerType === 'skype' ? 'Skype' : 'Call'}
                </Button>
            )}
            {shouldShowSmsButton && phone && phone.id && (
                <SmsSidebarButton
                    entityId={props.id}
                    entityPageName={props.name}
                    openedFromPhoneNumber={phone.id}
                    buttonVariant='upgrade'
                    buttonSize='small'
                    buttonIcon={MobilePhoneRegularIcon}
                    buttonText='Text'
                    shouldClickPreventDefault={true}
                />
            )}
            {shouldShowEmailButton && (
                <Button
                    icon={EnvelopeNewIcon}
                    size='small'
                    variant='upgrade'
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (props.email) {
                            openEmailModal(props.email);
                        }
                    }}
                >
                    Email
                </Button>
            )}
            {shouldShowDeliverabilityStatusBadge && (
                <DeliverabilityStatusBadge
                    deliverabilityStatus={deliverabilityStatus}
                    iconOnly={false}
                />
            )}
            <PhoneSettingsDialog
                isOpen={showSettingsDialog}
                onClose={() => setShowSettingsDialog(false)}
                anchorElementId={callButtonId}
            />
        </div>
    );
}
